// The purpose of this HOC is to provide currentSite data *WITHOUT* making any backend requests
// - this pre-supposes you have the regular withSiteContext HOC also on the page somewhere else
// - therefore the intent to not duplicate any backend requests

import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { selectors as licenseSelectors } from 'store/licenseManager'
import { selectors as contextSelectors } from 'auth/stores/userContext'
import {
  findSiteById,
  getProgramType,
  getSiteFromUrl,
  getDefaultSite,
  getSiteAndUserPreferences,
  isSiteSetup,
  getActiveSiteIdKeyForProgramType,
} from '../withSiteContext/helpers'

export default function withSiteContextLite(Component) {
  const innerComponent = props => {
    const {
      allSites,
      k5ActiveSites,
      msActiveSites,
      hsActiveSites,
      userContext,
    } = props

    const programType = getProgramType()

    // Get the appropriate active sites based on program type
    const activeSites = useMemo(() => {
      const isDigitalWellBeingRoute =
        typeof window !== 'undefined' &&
        window?.location?.pathname?.includes('/digital-well-being')

      let resolvedActiveSites
      switch (programType) {
        case 'highSchool':
          resolvedActiveSites = hsActiveSites
          break
        case 'middleSchool':
          resolvedActiveSites = msActiveSites
          break
        case 'k5':
          resolvedActiveSites = k5ActiveSites
          break
        default:
          resolvedActiveSites = []
          break
      }

      if (
        isDigitalWellBeingRoute &&
        Array.isArray(resolvedActiveSites) &&
        resolvedActiveSites.length === 0 &&
        Array.isArray(allSites)
      ) {
        return allSites
      }

      return resolvedActiveSites
    }, [programType, hsActiveSites, msActiveSites, k5ActiveSites, allSites])

    // Determine current site based on URL parameter or user context
    const currentSite = useMemo(() => {
      // Skip if we don't have active sites or program type
      if (!programType || !activeSites || activeSites.length === 0) {
        return null
      }

      // Get the correct active site ID key for the current program type
      const activeSiteIdKey = getActiveSiteIdKeyForProgramType(programType)
      const activeSiteIdFromContext = userContext?.[activeSiteIdKey]

      // Priority 1: Site from URL query parameter (if present)
      const siteFromUrl = getSiteFromUrl(activeSites)

      // Priority 2: Persisted site from Redux state
      const siteFromContext = activeSiteIdFromContext
        ? findSiteById(activeSites, activeSiteIdFromContext)
        : null

      // Priority 3: Default site (first site alphabetically)
      const defaultSite = getDefaultSite(activeSites)

      // Choose the current site based on priorities
      let selectedSite
      if (siteFromUrl) {
        selectedSite = siteFromUrl
      } else if (siteFromContext) {
        selectedSite = siteFromContext
      } else {
        selectedSite = defaultSite
      }

      if (!selectedSite) {
        return null
      }

      // Add setup information
      const siteSetup = isSiteSetup(selectedSite, programType, userContext)
      const siteWithSetupInfo = {
        ...selectedSite,
        isSetup: siteSetup,
      }

      // Add user preferences if allSites is available
      const siteWithPreferences = allSites
        ? getSiteAndUserPreferences(siteWithSetupInfo, allSites, programType)
        : siteWithSetupInfo

      return siteWithPreferences
    }, [programType, activeSites, userContext, allSites])

    // Render the wrapped component with currentSite
    if (programType) {
      return (
        <Component
          {...props}
          activeSites={activeSites}
          currentSite={currentSite}
        />
      )
    }

    // If no program type detected, render component without site context
    return <Component {...props} />
  }

  innerComponent.displayName = 'withSiteContextLite'

  const mapStateToProps = state => {
    const hsProgramCode = 'HSP'
    const activeSites = licenseSelectors?.selectActiveSites(state) || null

    // Initialize with null to distinguish between "not fetched" and "fetched but empty"
    let k5ActiveSites = null
    let msActiveSites = null
    let hsActiveSites = null

    // Only process activeSites if it's an array (not null)
    if (Array.isArray(activeSites)) {
      // Filter active sites by programCodes (site-centric) and valid claims
      // Sites have programCodes derived from capabilities (platform scope) OR from licenses (legacy)
      // Helper to extract programCodes from either site.programCodes (new) or licenses (legacy)
      const getProgramCodes = site => {
        // Prefer direct programCodes (from adapter)
        // The adapter should already filter out expired licenses, so we trust this
        if (Array.isArray(site.programCodes) && site.programCodes.length > 0) {
          return site.programCodes
        }
        // Fallback to extracting from licenses (legacy format)
        // IMPORTANT: Only extract from non-expired licenses to avoid showing sites
        // with expired program access (e.g., expired K5P license but active MSP license)
        if (Array.isArray(site.licenses)) {
          const codes = new Set()
          site.licenses.forEach(license => {
            if (
              !license?.isExpired &&
              license?.product?.programCodes &&
              Array.isArray(license.product.programCodes)
            ) {
              license.product.programCodes.forEach(code => codes.add(code))
            }
          })
          return Array.from(codes)
        }
        return []
      }

      k5ActiveSites = activeSites.filter(s => {
        const programCodes = getProgramCodes(s)
        return (
          programCodes.includes('K5P') &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin'))
        )
      })
      msActiveSites = activeSites.filter(s => {
        const programCodes = getProgramCodes(s)
        return (
          programCodes.includes('MSP') &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin'))
        )
      })
      hsActiveSites = activeSites.filter(s => {
        const programCodes = getProgramCodes(s)
        return (
          programCodes.includes(hsProgramCode) &&
          (s?.claims?.includes('Teacher') || s?.claims?.includes('Admin'))
        )
      })
    }

    return {
      allSites: activeSites,
      k5ActiveSites,
      msActiveSites,
      hsActiveSites,
      userContext: contextSelectors.selectProgramContextWithSiteSelection(
        state,
      ),
    }
  }

  return connect(mapStateToProps)(innerComponent)
}
